import React, { useState, useEffect } from 'react';
import { X } from 'lucide-react';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001/api';

const ProjectTagsInput = ({ tags = [], onChange }) => {
  const [inputValue, setInputValue] = useState('');
  const [suggestions, setSuggestions] = useState([]);
  const [allTags, setAllTags] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);

  useEffect(() => {
    fetchAllTags();
  }, []);

  const fetchAllTags = async () => {
    try {
      const response = await axios.get(`${API}/projects/tags`);
      setAllTags(response.data.tags || []);
    } catch (error) {
      console.error('Failed to fetch tags:', error);
    }
  };

  const handleInputChange = (e) => {
    const value = e.target.value;
    setInputValue(value);

    if (value.trim()) {
      // Filter suggestions based on input
      const filtered = allTags
        .filter(tag => 
          tag.name.toLowerCase().includes(value.toLowerCase()) &&
          !tags.includes(tag.name)
        )
        .slice(0, 5);
      setSuggestions(filtered);
      setShowSuggestions(true);
    } else {
      setShowSuggestions(false);
    }
  };

  const addTag = (tagName) => {
    if (tagName.trim() && !tags.includes(tagName.trim())) {
      onChange([...tags, tagName.trim()]);
      setInputValue('');
      setShowSuggestions(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && inputValue.trim()) {
      e.preventDefault();
      addTag(inputValue);
    } else if (e.key === 'Backspace' && !inputValue && tags.length > 0) {
      // Remove last tag on backspace if input is empty
      onChange(tags.slice(0, -1));
    }
  };

  const removeTag = (tagToRemove) => {
    onChange(tags.filter(tag => tag !== tagToRemove));
  };

  return (
    <div className="space-y-2">
      <div className="flex flex-wrap gap-2 p-2 border border-gray-300 dark:border-gray-600 rounded-lg min-h-[42px] focus-within:ring-2 focus-within:ring-cyan-500 dark:focus-within:ring-cyan-400 bg-white dark:bg-gray-700 transition-colors">
        {tags.map((tag, index) => (
          <span
            key={index}
            className="inline-flex items-center gap-1 px-2 py-1 bg-cyan-100 dark:bg-cyan-900/30 text-cyan-700 dark:text-cyan-300 rounded-md text-sm font-medium border border-cyan-200 dark:border-cyan-800 transition-colors"
          >
            {tag}
            <button
              onClick={() => removeTag(tag)}
              className="hover:text-cyan-900 dark:hover:text-cyan-200 transition-colors"
              type="button"
            >
              <X className="w-3 h-3" />
            </button>
          </span>
        ))}
        <input
          type="text"
          value={inputValue}
          onChange={handleInputChange}
          onKeyDown={handleKeyDown}
          onFocus={() => inputValue && setShowSuggestions(true)}
          onBlur={() => setTimeout(() => setShowSuggestions(false), 200)}
          placeholder={tags.length === 0 ? "Add tags..." : ""}
          className="flex-1 min-w-[120px] outline-none bg-transparent text-sm text-gray-900 dark:text-gray-100 placeholder:text-gray-500 dark:placeholder:text-gray-400 transition-colors"
        />
      </div>

      {/* Suggestions Dropdown */}
      {showSuggestions && suggestions.length > 0 && (
        <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg max-h-40 overflow-y-auto transition-colors">
          {suggestions.map((tag, index) => (
            <button
              key={index}
              type="button"
              onClick={() => addTag(tag.name)}
              className="w-full text-left px-3 py-2 hover:bg-cyan-50 dark:hover:bg-cyan-900/30 flex items-center justify-between text-sm text-gray-900 dark:text-gray-100 transition-colors"
            >
              <span>{tag.name}</span>
              <span className="text-xs text-gray-500 dark:text-gray-400 transition-colors">{tag.count} project{tag.count !== 1 ? 's' : ''}</span>
            </button>
          ))}
        </div>
      )}

      <div className="text-xs text-gray-500 dark:text-gray-400 transition-colors">
        Press Enter to add tag • {tags.length} tag{tags.length !== 1 ? 's' : ''}
      </div>
    </div>
  );
};

export default ProjectTagsInput;
